import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { supabase } from '../../lib/supabaseClient';
import { authedJson } from '../../utils/adminApi';
import { uploadImageToSiteMedia } from '../../utils/adminMedia';

export default function HomeContentEditor() {
  const normalizeAboutGalleryImages = React.useCallback((images) => {
    const list = Array.isArray(images) ? images.filter((img) => typeof img === 'string') : [];
    const withDefaults = [...list];
    while (withDefaults.length < 3) withDefaults.push('');
    return withDefaults.slice(0, 6);
  }, []);

  const defaultHome = React.useMemo(() => ({
    heroSubtext: 'A space to play, connect, and talk real. Then we stream.',
    aboutTitle: 'Play. Talk. Stream.',
    aboutImage: '',
    aboutGalleryImages: ['', '', ''],
    aboutParagraphs: [
      'GameMeetsWorld is where people come to play, have fun, and talk about the real stuff — from culture and trends to life and gaming.',
      'We bring the community together for interviews, live discussions, and energy-filled hangouts that feel like home.',
      'Then we stream it. Big moments, real conversations, and a vibe that keeps the community connected.'
    ],
    streamsHeading: 'Stream The Vibe',
    storePreviewTitle: 'Drop Culture Only',
    storePreviewSubtext: 'Limited merch inspired by the stream, the guests, and the community.',
    storeHeroViewImage: '',
    storeHeroCollectionImage: '',
    storeNewsletterImage: '',
    storeLatestImage: '',
    features: [
      { title: 'Play Sessions', desc: 'Squads, challenges, and casual nights with the community' },
      { title: 'Real Conversations', desc: 'Trends, culture, and life — the stuff people actually talk about' },
      { title: 'Live Interviews', desc: 'Guests, creators, and voices shaping the scene' },
      { title: 'Community Vibes', desc: 'Hangouts that feel like a safe, fun home base' },
      { title: 'Stream Nights', desc: 'Highlights and live shows that bring everyone together' },
      { title: 'Hall of Fame', desc: 'Celebrating the guests who came on the stream' }
    ]
  }), []);

  const [content, setContent] = useState(defaultHome);
  const [contentId, setContentId] = useState(null);
  const [feedback, setFeedback] = useState({ error: '', success: '' });

  useEffect(() => {
    const load = async () => {
      const { data } = await supabase.auth.getSession();
      const token = data?.session?.access_token;
      if (!token) return;
      const res = await fetch('/api/admin/homepage', { headers: { Authorization: `Bearer ${token}` } });
      const json = await res.json();
      if (json) {
        setContentId(json.id);
        setContent({
          heroSubtext: json.hero_subtext ?? defaultHome.heroSubtext,
          aboutTitle: json.about_title ?? defaultHome.aboutTitle,
          aboutImage: json.about_image_url ?? defaultHome.aboutImage,
          aboutGalleryImages: normalizeAboutGalleryImages(json.about_gallery_images || defaultHome.aboutGalleryImages),
          aboutParagraphs: json.about_paragraphs ?? defaultHome.aboutParagraphs,
          streamsHeading: json.streams_heading ?? defaultHome.streamsHeading,
          storePreviewTitle: json.store_preview_title ?? defaultHome.storePreviewTitle,
          storePreviewSubtext: json.store_preview_subtext ?? defaultHome.storePreviewSubtext,
          storeHeroViewImage: json.store_hero_view_image_url ?? defaultHome.storeHeroViewImage,
          storeHeroCollectionImage: json.store_hero_collection_image_url ?? defaultHome.storeHeroCollectionImage,
          storeNewsletterImage: json.store_newsletter_image_url ?? defaultHome.storeNewsletterImage,
          storeLatestImage: json.store_latest_image_url ?? defaultHome.storeLatestImage,
          features: json.features ?? defaultHome.features
        });
      }
    };
    load();
  }, [defaultHome, normalizeAboutGalleryImages]);

  const updateParagraph = (idx, value) => {
    const updated = [...content.aboutParagraphs];
    updated[idx] = value;
    setContent({ ...content, aboutParagraphs: updated });
  };

  const updateAboutGalleryImage = (idx, value) => {
    const updated = [...normalizeAboutGalleryImages(content.aboutGalleryImages)];
    updated[idx] = value;
    setContent({ ...content, aboutGalleryImages: updated });
  };

  const addFeature = () => {
    setContent({ ...content, features: [{ title: '', desc: '' }, ...content.features] });
  };

  const updateFeature = (idx, key, value) => {
    const updated = [...content.features];
    updated[idx] = { ...updated[idx], [key]: value };
    setContent({ ...content, features: updated });
  };

  const deleteFeature = (idx) => {
    if (!window.confirm('Delete this feature?')) return;
    setContent({ ...content, features: content.features.filter((_, i) => i !== idx) });
  };

  const saveHome = async () => {
    setFeedback({ error: '', success: '' });
    const body = {
      hero_subtext: content.heroSubtext,
      about_title: content.aboutTitle,
      about_image_url: content.aboutImage,
      about_gallery_images: (content.aboutGalleryImages || []).filter(Boolean),
      about_paragraphs: content.aboutParagraphs,
      streams_heading: content.streamsHeading,
      store_preview_title: content.storePreviewTitle,
      store_preview_subtext: content.storePreviewSubtext,
      store_hero_view_image_url: content.storeHeroViewImage,
      store_hero_collection_image_url: content.storeHeroCollectionImage,
      store_newsletter_image_url: content.storeNewsletterImage,
      store_latest_image_url: content.storeLatestImage,
      features: content.features
    };
    try {
      if (contentId) {
        await authedJson('/api/admin/homepage', {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: contentId, ...body })
        });
      } else {
        const created = await authedJson('/api/admin/homepage', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body)
        });
        if (created?.id) {
          setContentId(created.id);
        }
      }
      setFeedback({ error: '', success: 'Homepage content saved.' });
    } catch (error) {
      setFeedback({ error: error.message, success: '' });
    }
  };

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.2 }} className="bg-[#0A0E11] rounded-xl p-8 border border-[#A1FF4F]/10">
      <h2 className="text-2xl font-bold mb-4">Homepage Content</h2>
      {feedback.error && <p className="mb-3 text-red-300 text-sm">{feedback.error}</p>}
      {feedback.success && <p className="mb-3 text-[#A1FF4F] text-sm">{feedback.success}</p>}
      <div className="space-y-4">
        <input
          value={content.heroSubtext}
          onChange={(e)=>setContent({ ...content, heroSubtext: e.target.value })}
          placeholder="hero subtext"
          className="p-3 bg-[#0F1419] rounded w-full"
        />
        <input
          value={content.aboutTitle}
          onChange={(e)=>setContent({ ...content, aboutTitle: e.target.value })}
          placeholder="about title"
          className="p-3 bg-[#0F1419] rounded w-full"
        />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <input
            value={content.aboutImage}
            onChange={(e)=>setContent({ ...content, aboutImage: e.target.value })}
            placeholder="about section image url"
            className="p-3 bg-[#0F1419] rounded w-full"
          />
          <input
            type="file"
            accept="image/*"
            onChange={async (e) => {
              const file = e.target.files?.[0];
              if (!file) return;
              try {
                const url = await uploadImageToSiteMedia(file, 'home-about');
                setContent({ ...content, aboutImage: url });
              } catch (error) {
                setFeedback({ error: error.message, success: '' });
              }
            }}
            className="p-3 bg-[#0F1419] rounded text-white/70"
          />
        </div>
        <div className="space-y-2">
          <p className="text-sm text-white/70">About carousel images</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {(content.aboutGalleryImages || []).map((img, idx) => (
              <div key={idx} className="space-y-2">
                <input
                  value={img}
                  onChange={(e)=>updateAboutGalleryImage(idx, e.target.value)}
                  placeholder={`carousel image ${idx + 1} url`}
                  className="p-3 bg-[#0F1419] rounded w-full"
                />
                <input
                  type="file"
                  accept="image/*"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    try {
                      const url = await uploadImageToSiteMedia(file, 'home-about-carousel');
                      updateAboutGalleryImage(idx, url);
                    } catch (error) {
                      setFeedback({ error: error.message, success: '' });
                    }
                  }}
                  className="p-3 bg-[#0F1419] rounded text-white/70 w-full"
                />
              </div>
            ))}
          </div>
        </div>
        {content.aboutParagraphs.map((para, idx) => (
          <textarea
            key={idx}
            value={para}
            onChange={(e)=>updateParagraph(idx, e.target.value)}
            placeholder={`about paragraph ${idx + 1}`}
            className="p-3 bg-[#0F1419] rounded w-full min-h-[80px]"
          />
        ))}
        <input
          value={content.streamsHeading}
          onChange={(e)=>setContent({ ...content, streamsHeading: e.target.value })}
          placeholder="streams heading"
          className="p-3 bg-[#0F1419] rounded w-full"
        />
        <input
          value={content.storePreviewTitle}
          onChange={(e)=>setContent({ ...content, storePreviewTitle: e.target.value })}
          placeholder="home store preview title"
          className="p-3 bg-[#0F1419] rounded w-full"
        />
        <input
          value={content.storePreviewSubtext}
          onChange={(e)=>setContent({ ...content, storePreviewSubtext: e.target.value })}
          placeholder="home store preview subtext"
          className="p-3 bg-[#0F1419] rounded w-full"
        />
        <div className="space-y-3 bg-white/5 p-4 rounded">
          <p className="text-sm font-semibold text-white/80">Store Campaign Media</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <input
              value={content.storeHeroViewImage}
              onChange={(e)=>setContent({ ...content, storeHeroViewImage: e.target.value })}
              placeholder="store hero view image url"
              className="p-3 bg-[#0F1419] rounded w-full"
            />
            <input
              type="file"
              accept="image/*"
              onChange={async (e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                try {
                  const url = await uploadImageToSiteMedia(file, 'home-store');
                  setContent({ ...content, storeHeroViewImage: url });
                } catch (error) {
                  setFeedback({ error: error.message, success: '' });
                }
              }}
              className="p-3 bg-[#0F1419] rounded text-white/70"
            />
            <input
              value={content.storeHeroCollectionImage}
              onChange={(e)=>setContent({ ...content, storeHeroCollectionImage: e.target.value })}
              placeholder="store hero new collection image url"
              className="p-3 bg-[#0F1419] rounded w-full"
            />
            <input
              type="file"
              accept="image/*"
              onChange={async (e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                try {
                  const url = await uploadImageToSiteMedia(file, 'home-store');
                  setContent({ ...content, storeHeroCollectionImage: url });
                } catch (error) {
                  setFeedback({ error: error.message, success: '' });
                }
              }}
              className="p-3 bg-[#0F1419] rounded text-white/70"
            />
            <input
              value={content.storeNewsletterImage}
              onChange={(e)=>setContent({ ...content, storeNewsletterImage: e.target.value })}
              placeholder="store newsletter image url"
              className="p-3 bg-[#0F1419] rounded w-full"
            />
            <input
              type="file"
              accept="image/*"
              onChange={async (e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                try {
                  const url = await uploadImageToSiteMedia(file, 'home-store');
                  setContent({ ...content, storeNewsletterImage: url });
                } catch (error) {
                  setFeedback({ error: error.message, success: '' });
                }
              }}
              className="p-3 bg-[#0F1419] rounded text-white/70"
            />
            <input
              value={content.storeLatestImage}
              onChange={(e)=>setContent({ ...content, storeLatestImage: e.target.value })}
              placeholder="store latest section image url"
              className="p-3 bg-[#0F1419] rounded w-full"
            />
            <input
              type="file"
              accept="image/*"
              onChange={async (e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                try {
                  const url = await uploadImageToSiteMedia(file, 'home-store');
                  setContent({ ...content, storeLatestImage: url });
                } catch (error) {
                  setFeedback({ error: error.message, success: '' });
                }
              }}
              className="p-3 bg-[#0F1419] rounded text-white/70"
            />
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button type="button" onClick={addFeature} className="px-4 py-2 bg-[#A1FF4F]/20 text-[#A1FF4F] rounded">
            Add Feature
          </button>
          <button type="button" onClick={saveHome} className="px-4 py-2 bg-white/10 rounded">
            Save Homepage
          </button>
        </div>
        <div className="space-y-3">
          {content.features.map((feature, idx) => (
            <div key={idx} className="grid grid-cols-1 md:grid-cols-3 gap-3 bg-white/5 p-3 rounded">
              <input
                value={feature.title}
                onChange={(e)=>updateFeature(idx, 'title', e.target.value)}
                placeholder="feature title"
                className="p-3 bg-[#0F1419] rounded"
              />
              <input
                value={feature.desc}
                onChange={(e)=>updateFeature(idx, 'desc', e.target.value)}
                placeholder="feature description"
                className="p-3 bg-[#0F1419] rounded md:col-span-2"
              />
              <div className="md:col-span-3 flex justify-end">
                <button onClick={()=>deleteFeature(idx)} className="px-3 py-2 bg-red-600/20 rounded">Delete</button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </motion.div>
  );
}
